import api from './api';
import type {
  ApiResponse,
  Announcement,
  AnnouncementAdminData,
  AnnouncementLevel,
  AnnouncementTemplate,
} from '../types';

// ============================================
// Lecture (utilisateur connecté)
// ============================================

/**
 * Récupère l'annonce en cours. `data` vaut `null` quand il n'y en a pas.
 */
export async function getActiveAnnouncement(): Promise<ApiResponse<Announcement | null>> {
  const response = await api.get('/announcement');
  return response.data;
}

// ============================================
// Administration
// ============================================

export async function getAnnouncementAdmin(
  page = 1,
  pageSize = 10
): Promise<ApiResponse<AnnouncementAdminData>> {
  const response = await api.get('/admin/announcement', { params: { page, pageSize } });
  return response.data;
}

export interface AnnouncementInput {
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
  /** Publier en activant aussitôt le bandeau. */
  activate: boolean;
}

export async function createAnnouncement(
  input: AnnouncementInput
): Promise<ApiResponse<Announcement>> {
  const response = await api.post('/admin/announcement', input);
  return response.data;
}

export async function updateAnnouncement(
  id: string,
  input: AnnouncementInput
): Promise<ApiResponse<Announcement>> {
  const response = await api.put(`/admin/announcement/${id}`, input);
  return response.data;
}

/** Active ou désactive une annonce sans repasser par l'éditeur. */
export async function setAnnouncementActive(
  id: string,
  active: boolean
): Promise<ApiResponse<Announcement>> {
  const response = await api.put(`/admin/announcement/${id}/active`, { active });
  return response.data;
}

export async function deleteAnnouncement(id: string): Promise<ApiResponse<void>> {
  const response = await api.delete(`/admin/announcement/${id}`);
  return response.data;
}

export interface TemplateInput {
  name: string;
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
}

export async function createTemplate(
  input: TemplateInput
): Promise<ApiResponse<AnnouncementTemplate>> {
  const response = await api.post('/admin/announcement/templates', input);
  return response.data;
}

export async function updateTemplate(
  id: string,
  input: TemplateInput
): Promise<ApiResponse<AnnouncementTemplate>> {
  const response = await api.put(`/admin/announcement/templates/${id}`, input);
  return response.data;
}

export async function deleteTemplate(id: string): Promise<ApiResponse<void>> {
  const response = await api.delete(`/admin/announcement/templates/${id}`);
  return response.data;
}

/**
 * Traduit un fragment HTML pour pré-remplir l'autre langue.
 *
 * Renvoie une erreur 503 quand le service est indisponible ou non configuré :
 * l'appelant doit alors laisser l'admin saisir la traduction lui-même.
 */
export async function translate(
  html: string,
  targetLang: 'EN' | 'FR',
  sourceLang?: 'EN' | 'FR'
): Promise<ApiResponse<{ html: string }>> {
  const response = await api.post('/admin/announcement/translate', {
    html,
    targetLang,
    sourceLang,
  });
  return response.data;
}
